import ExcelJS from 'exceljs';

function sumChildrenAmounts(node) {
  let monthTotals = { ...(node.monthTotals || {}) };
  let yearTotals = { ...(node.yearTotals || {}) };

  const realChildren = (node.children || []).filter(c => !c.isSynthetic);
  realChildren.forEach(child => {
    const { monthTotals: childMonthTotals, yearTotals: childYearTotals } = sumChildrenAmounts(child);
    for (const locationId in childMonthTotals) {
      if (!monthTotals[locationId]) monthTotals[locationId] = 0;
      monthTotals[locationId] += childMonthTotals[locationId];
    }
    for (const locationId in childYearTotals) {
      if (!yearTotals[locationId]) yearTotals[locationId] = 0;
      yearTotals[locationId] += childYearTotals[locationId];
    }
  });

  node.monthTotals = monthTotals;
  node.yearTotals = yearTotals;

  return { monthTotals, yearTotals };
}

function flattenTree(data, level = 0, result = []) {
  data.forEach(node => {
    const alwaysInclude = [2, 3, 4].includes(node.id);
    const hasNonZeroTotals = Object.values(node.monthTotals).some(total => total !== 0) ||
                             Object.values(node.yearTotals).some(total => total !== 0);

    if (alwaysInclude || hasNonZeroTotals) {
      result.push({
        id: node.id,
        name: node.name,
        parent: node.parent,
        code: node.code,
        note: node.note,
        account_type_id: node.account_type_id,
        dgii_category_id: node.dgii_category_id,
        status: node.status,
        level: level,
        monthTotals: node.monthTotals,
        yearTotals: node.yearTotals,
        isParent: alwaysInclude
      });

      if (node.children && node.children.length > 0) {
        flattenTree(node.children, level + 1, result);
      }
    }
  });
  return result;
}
function addAmountsToAccounts(accounts, processed) {
  accounts.forEach(account => {
    let yearTotals = {};

    processed.forEach(procAccount => {
      if (procAccount.chart_of_account_id === account.id) {
        const locationId = procAccount.location_id;
        if (!yearTotals[locationId]) yearTotals[locationId] = 0;

        const yearTotal = parseFloat(procAccount.year_total) || 0;

        yearTotals[locationId] += yearTotal;
      }
    });

    account.yearTotals = yearTotals;

    // Si tiene hijos, añadir cantidades a los hijos
    if (account.children && account.children.length > 0) {
      addAmountsToAccounts(account.children, processed);
    }

    // Añadir nodos de subcategorías directamente bajo la cuenta principal
    let subcategoryMap = {};

    processed.forEach(procAccount => {
      if (procAccount.chart_of_account_id === account.id && procAccount.subcategory_id) {
        const locationId = procAccount.location_id;

        // Crear o actualizar la subcategoría
        if (!subcategoryMap[procAccount.subcategory_id]) {
          subcategoryMap[procAccount.subcategory_id] = {
            id: procAccount.subcategory_id,
            name: procAccount.subcategory_name,
            parent: account.id,
            children: [],
            yearTotals: {},
            monthTotals: {},
            isSynthetic: true,
            level: account.level + 1
          };
        }

        let subcategoryNode = subcategoryMap[procAccount.subcategory_id];

        // Actualizar los montos en la subcategoría
        subcategoryNode.yearTotals[locationId] = (subcategoryNode.yearTotals[locationId] || 0) + parseFloat(procAccount.year_total) || 0;
      }
    });

    // Añadir las subcategorías al árbol de cuentas principal
    for (let subcategoryId in subcategoryMap) {
      account.children.push(subcategoryMap[subcategoryId]);
    }
  });
}
export const generateEstadoSituacionExcel = async (all_accounts, processed_accounts, info, all_locations, accumulatedAmounts) => {


  addAmountsToAccounts(all_accounts, processed_accounts);

  all_accounts.forEach(node => sumChildrenAmounts(node));

  const flattenedData = flattenTree(all_accounts);

  const locationIds = Array.from(new Set(processed_accounts.map(acc => acc.location_id))).sort((a, b) => a - b);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Data');

  const headerStyle = {
    font: { name: 'Arial', size: 14, bold: true },
    alignment: { vertical: 'middle', horizontal: 'center' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D3D3D3' } }
  };

  worksheet.addRow([]);
  worksheet.addRow([]);
  worksheet.addRow([]);
  worksheet.addRow([]);

  worksheet.mergeCells('A1:H1');
  worksheet.mergeCells('A2:H2');
  worksheet.mergeCells('A3:H3');
  worksheet.mergeCells('A4:H4');

  worksheet.getCell('A1').value = info.business_name;
  worksheet.getCell('A2').value = 'Estado de Situación Financiera';
  worksheet.getCell('A3').value = `Valor expresado en (${info.currency_code})`;
  worksheet.getCell('A4').value = `Hasta la fecha ${info.date_to}`;

  worksheet.getCell('A1').font = headerStyle.font;
  worksheet.getCell('A1').alignment = headerStyle.alignment;
  worksheet.getCell('A1').fill = headerStyle.fill;

  worksheet.getCell('A2').font = headerStyle.font;
  worksheet.getCell('A2').alignment = headerStyle.alignment;
  worksheet.getCell('A2').fill = headerStyle.fill;

  worksheet.getCell('A3').font = headerStyle.font;
  worksheet.getCell('A3').alignment = headerStyle.alignment;
  worksheet.getCell('A3').fill = headerStyle.fill;

  worksheet.getCell('A4').font = headerStyle.font;
  worksheet.getCell('A4').alignment = headerStyle.alignment;
  worksheet.getCell('A4').fill = headerStyle.fill;

  worksheet.getRow(1).height = 20;
  worksheet.getRow(2).height = 20;
  worksheet.getRow(3).height = 20;
  worksheet.getRow(4).height = 20;

  const headerRow = worksheet.addRow(['Código cuenta', 'Nombre cuenta', ...locationIds.map(id => `${all_locations.find(l => l.id === id).desc_loc_long}`), 'Monto Acumulado']);
  headerRow.font = { bold: true };

  worksheet.columns = [
    { header: 'Código cuenta', key: 'code', width: 15 },
    { header: 'Nombre cuenta', key: 'name', width: 50 },
    ...locationIds.map(id => ({ header: `${all_locations.find(l => l.id === id).desc_loc_long}`, key: `year_${id}`, width: 30 })),
    { header: 'Monto Acumulado', key: 'total_year', width: 30 }
  ];

  const rowReferences = { year: [] };

  // La vista (estadoSituacionView + Status.vue) aplica un factor de "naturaleza" SOLO a las
  // cuentas raiz (isParent): activos +1, pasivos/patrimonio -1. Los hijos se muestran en
  // crudo, con su signo real. Replicamos exactamente eso para que el Excel salga igual que
  // la pantalla (raiz del lado credito en positivo, hijos tal cual).
  flattenedData.forEach((row, index) => {
    const indent = ' '.repeat(row.level * 4);
    const nature = row.isParent ? (row.id === 2 ? 1 : -1) : 1;
    const newRowData = {
      code: row.code,
      name: indent + row.name,
      total_year: Object.values(row.yearTotals).reduce((sum, value) => sum + value, 0) * nature
    };
    locationIds.forEach(id => {
      newRowData[`year_${id}`] = (row.yearTotals[id] || 0) * nature;
    });

    const newRow = worksheet.addRow(newRowData);

    if (row.isParent) {
      rowReferences.year.push(newRow.getCell('total_year').address);
      locationIds.forEach(id => {
        rowReferences[`year_${id}`] = rowReferences[`year_${id}`] || [];
        rowReferences[`year_${id}`].push(newRow.getCell(`year_${id}`).address);
      });
      newRow.font = { bold: true, size: 16 };
      locationIds.forEach((id, index) => {
        newRow.getCell(3 + index).alignment = { horizontal: 'right' };
      });
    } else {
      if (row.level === 1) {
        newRow.font = { bold: true };
      }
      locationIds.forEach((id, index) => {
        newRow.getCell(3 + index).alignment = { horizontal: 'right' };
      });
    }
  });

  const patrimonioNode = all_accounts.find(node => node.id === 4);
  const pasivosNode = all_accounts.find(node => node.id === 3);

  // La utilidad, el patrimonio y los pasivos son de naturaleza credito: los calculos se
  // hacen en crudo (con su signo real) y se invierten (-1) solo al mostrarlos, igual que
  // la vista. Asi el balance queda cuadrado internamente y el Excel coincide con la pantalla.
  const accumulatedTotalRaw = Object.values(accumulatedAmounts).reduce((sum, value) => sum + value, 0);
  const accumulatedAmountsRowData = {
    code: '',
    name: 'Utilidad Periodo Actual',
    total_year: accumulatedTotalRaw * -1
  };

  locationIds.forEach(id => {
    accumulatedAmountsRowData[`year_${id}`] = (accumulatedAmounts[id] || 0) * -1;
  });

  const utilidadPeriodoActualRow = worksheet.addRow(accumulatedAmountsRowData);
  utilidadPeriodoActualRow.font = { bold: true, size: 16 };

  worksheet.addRow(['', '', '', '', '', '', '', '', '', '']);
  worksheet.addRow(['', '', '', '', '', '', '', '', '', '']);

  const patrimonioTotalRaw = Object.values(patrimonioNode.yearTotals).reduce((sum, value) => sum + value, 0);
  const participacionTotalRaw = patrimonioTotalRaw + accumulatedTotalRaw;

  const totalParticipacionRowData = {
    code: '',
    name: 'TOTAL PARTICIPACIÓN',
    total_year: participacionTotalRaw * -1
  };

  locationIds.forEach(id => {
    const participacionRaw = (patrimonioNode.yearTotals[id] || 0) + (accumulatedAmounts[id] || 0);
    totalParticipacionRowData[`year_${id}`] = participacionRaw * -1;
  });

  const totalParticipacionRow = worksheet.addRow(totalParticipacionRowData);
  totalParticipacionRow.font = { bold: true, size: 16 };

  worksheet.addRow(['', '', '', '', '', '', '', '', '', '']);

  const pasivosTotalRaw = Object.values(pasivosNode.yearTotals).reduce((sum, value) => sum + value, 0);
  const pasivosParticipacionTotalRaw = participacionTotalRaw + pasivosTotalRaw;

  const totalPasivosParticipacionRowData = {
    code: '',
    name: 'TOTAL PASIVOS Y PARTICIPACIÓN',
    total_year: pasivosParticipacionTotalRaw * -1
  };

  locationIds.forEach(id => {
    const participacionRaw = (patrimonioNode.yearTotals[id] || 0) + (accumulatedAmounts[id] || 0);
    const pasivosParticipacionRaw = participacionRaw + (pasivosNode.yearTotals[id] || 0);
    totalPasivosParticipacionRowData[`year_${id}`] = pasivosParticipacionRaw * -1;
  });

  const totalPasivosParticipacionRow = worksheet.addRow(totalPasivosParticipacionRowData);
  totalPasivosParticipacionRow.font = { bold: true, size: 16 };

  const accountingFormat = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';
  worksheet.getCell('A1').value = info.business_name;
  worksheet.getCell('A2').value = 'Estado de Situación Financiera';
  worksheet.getCell('A3').value = `Valor expresado en (${info.currency_code})`;
  worksheet.getCell('A4').value = `Hasta la fecha ${info.date_to}`;
  worksheet.eachRow({ includeEmpty: true }, row => {
    row.eachCell({ includeEmpty: true }, cell => {
      const currentFont = cell.font;
      const currentAlignment = cell.alignment;

      cell.numFmt = accountingFormat;

      cell.font = currentFont;
      cell.alignment = currentAlignment;
    });
  });

  flattenedData.forEach((row, index) => {
    const currentRow = worksheet.getRow(index + 6);
    currentRow.outlineLevel = row.level;
  });

  worksheet.properties.outlineProperties = {
    summaryBelow: false,
    summaryRight: false,
  };

  locationIds.forEach((id, index) => {
    const colIndex = 3 + index;
    worksheet.getColumn(colIndex).outlineLevel = 1;
  });

  flattenedData.forEach((row, index) => {
    const currentRow = worksheet.getRow(index + 6);
    if (!row.isParent) {
      let hasValue = false;
      locationIds.forEach((id, locIndex) => {
        if (currentRow.getCell(3 + locIndex).value) {
          currentRow.getCell(3 + locIndex).border = {
            bottom: { style: 'thin' }
          };
          hasValue = true;
        }
      });
      if (currentRow.getCell('total_year').value) {
        currentRow.getCell('total_year').border = {
          bottom: { style: 'thin' }
        };
        hasValue = true;
      }

      if (hasValue) {
        if (index + 1 < flattenedData.length && flattenedData[index + 1].isParent) {
          locationIds.forEach((id, locIndex) => {
            if (currentRow.getCell(3 + locIndex).value) {
              currentRow.getCell(3 + locIndex).border = {
                bottom: { style: 'double' }
              };
            }
          });
          if (currentRow.getCell('total_year').value) {
            currentRow.getCell('total_year').border = {
              bottom: { style: 'double' }
            };
          }
        }
      }
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  let nameFile = `Estado de Situación - ${info.business_name} - Hasta la fecha ${info.date_to}`;
  link.download = `${nameFile}.xlsx`;
  link.click();
};
